/**
 * Dashboard plan-usage API (ADR-0263 S3) — merchant read of the
 * account's v2 plan assignment and current-month usage per site.
 *
 * Read-only. `active: false` means every site is `plan_source: 'none'`
 * (the default while the v2 catalog is draft); the UI hides itself.
 */

import type { HttpClient } from './http-client.js';

export type PlanUsageDimension =
  | 'page_loads_monthly'
  | 'orders_monthly'
  | 'assistant_requests_monthly';

export type PlanUsageState = 'ok' | 'near' | 'over' | 'unlimited';

export interface PlanUsageMeter {
  readonly dimension: PlanUsageDimension;
  readonly scope: 'site' | 'account';
  readonly usage: number;
  readonly limit: number | null;
  readonly base_limit: number | null;
  readonly addon_headroom: number;
  readonly percent: number | null;
  readonly state: PlanUsageState;
}

export interface PlanUsagePlanRef {
  readonly plan_id: string;
  readonly slug: string;
  readonly name: string;
}

export interface PlanUsagePending {
  readonly plan: PlanUsagePlanRef;
  readonly effective_at: string;
}

export interface PlanUsageSite {
  readonly site_id: string;
  readonly plan: PlanUsagePlanRef | null;
  readonly plan_source: 'assigned' | 'default' | 'agency' | 'none';
  readonly pending: PlanUsagePending | null;
  readonly features: readonly string[];
  readonly page_loads: PlanUsageMeter;
  readonly orders: PlanUsageMeter;
}

export interface PlanUsageAgencyInfo {
  readonly agency_account_id: string;
  readonly agency_name: string;
}

export interface PlanUsageAccountSummary {
  readonly plan: PlanUsagePlanRef | null;
  readonly plan_source: 'assigned' | 'default' | 'agency' | 'none';
  readonly features: readonly string[];
  /** ADR §2.3 — the ONE account-level assistant_requests reading.
   *  Null when the account is not on v2 yet. */
  readonly assistant_requests: PlanUsageMeter | null;
  /** ADR §2.6 — agency context for an agency-paid client account.
   *  Null when the client pays for itself. The dashboard section
   *  labels sites with `plan_source: 'agency'` using this name. */
  readonly agency: PlanUsageAgencyInfo | null;
}

export interface PlanUsageResponse {
  readonly account_id: string;
  readonly active: boolean;
  readonly period_start: string;
  readonly period_end: string;
  readonly account: PlanUsageAccountSummary;
  readonly sites: readonly PlanUsageSite[];
}

export type PlanUsageResult =
  | { readonly kind: 'ok'; readonly data: PlanUsageResponse }
  | { readonly kind: 'error' };

export async function getPlanUsage(http: HttpClient): Promise<PlanUsageResult> {
  try {
    const res = await http.request<PlanUsageResponse>({
      method: 'GET',
      path: '/v1/plan-usage',
    });
    if (res === undefined) return { kind: 'error' };
    return { kind: 'ok', data: res };
  } catch {
    return { kind: 'error' };
  }
}
